import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Trash2, Plus } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export const GoogleAccess = () => {
  const [data, setData] = useState({ allowed: [], users: [] });
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const headers = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }), []);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/auth/google/allowed`, { headers: headers() });
      setData(res.data);
    } catch {
      toast.error("Error cargando los accesos con Google");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/auth/google/allowed`, { email: email.trim() }, { headers: headers() });
      toast.success(`${email.trim().toLowerCase()} autorizado`);
      setEmail("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo autorizar el correo");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (addr) => {
    try {
      await axios.delete(`${API}/api/auth/google/allowed/${encodeURIComponent(addr)}`, { headers: headers() });
      toast.success(`${addr} eliminado`);
      await load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <Card data-testid="google-access-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" /> Acceso con Google
        </CardTitle>
        <CardDescription>
          Solo pueden entrar con Google los usuarios ya registrados y los correos que autorices aquí. Cualquier otra cuenta es rechazada.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={add} className="flex gap-2">
          <Input
            type="email"
            placeholder="nombre@gmail.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="google-allowed-email-input"
          />
          <Button type="submit" disabled={saving || !email.trim()} data-testid="google-allowed-add-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            <span className="ml-1 hidden sm:inline">Autorizar</span>
          </Button>
        </form>

        {loading ? (
          <div className="flex items-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Cargando…</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs uppercase text-muted-foreground mb-2">Correos autorizados ({data.allowed.length})</p>
              {data.allowed.length === 0 && <p className="text-muted-foreground" data-testid="google-allowed-empty">Ninguno todavía.</p>}
              <ul className="space-y-1" data-testid="google-allowed-list">
                {data.allowed.map((a) => (
                  <li key={a.email} className="flex items-center justify-between bg-muted/30 rounded px-2 py-1.5" data-testid={`google-allowed-${a.email}`}>
                    <span className="truncate">{a.email}</span>
                    <button type="button" onClick={() => remove(a.email)} className="text-red-600 hover:bg-red-50 rounded p-1" title="Quitar" data-testid={`google-allowed-remove-${a.email}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground mb-2">Usuarios registrados (siempre pueden entrar)</p>
              <ul className="space-y-1" data-testid="google-users-list">
                {data.users.map((u) => (
                  <li key={u.email} className="flex items-center justify-between bg-muted/30 rounded px-2 py-1.5">
                    <span className="truncate">{u.email}</span>
                    <span className="text-[10px] uppercase text-muted-foreground">{u.auth_provider === "google" ? "Google" : "Contraseña"}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GoogleAccess;
